import React, { useState, useEffect } from 'react';
import { timeEntriesAPI } from '../services/api';
import { Check, X, Eye, Filter, Search, CheckSquare, Trash2, MessageSquare, List, LayoutGrid } from 'lucide-react';

const Approvals = () => {
  const [pendingEntries, setPendingEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filter, setFilter] = useState('PENDING');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isBulkAction, setIsBulkAction] = useState(false);

  // Pagination and View states
  const [page, setPage] = useState(1);
  const [size] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'block'
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => { 
    setPage(1);
  }, [filter, activeSearch]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [page, filter, activeSearch]);

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const response = await timeEntriesAPI.getPendingApprovals({ 
        status: filter,
        search: activeSearch,
        page: page,
        size: size
      });
      setPendingEntries(response.data.data || []);
      if (response.data.pagination) {
        setPaginationInfo(response.data.pagination);
      }
      setSelectedIds([]); // Clear selection on refresh/filter change
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (entryId) => {
    try {
      await timeEntriesAPI.approve(entryId, { status: 'APPROVED' });
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving entry:', error);
      alert('Failed to approve entry');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    try {
      if (isBulkAction) {
        await timeEntriesAPI.bulkReject(selectedIds, rejectionReason);
      } else {
        await timeEntriesAPI.approve(selectedEntry.id, {
          status: 'REJECTED',
          rejection_reason: rejectionReason,
        });
      }
      setShowRejectModal(false);
      setSelectedEntry(null);
      setRejectionReason('');
      setIsBulkAction(false);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting entry:', error);
      alert('Failed to reject entries');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await timeEntriesAPI.addComment(selectedEntry.id, newComment);
      setNewComment('');
      // Refresh entries to show the new comment
      const response = await timeEntriesAPI.getPendingApprovals({ status: filter, search: activeSearch });
      setPendingEntries(response.data.data || []);
      const updatedEntry = (response.data.data || []).find(e => e.id === selectedEntry.id);
      if (updatedEntry) {
        setSelectedEntry(updatedEntry);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to approve ${selectedIds.length} selected entries?`)) return;

    setIsBulkApproving(true);
    try {
      await timeEntriesAPI.bulkApprove(selectedIds);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Bulk approval failed:', error);
      alert('Failed to approve selected entries');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleApproveAllFiltered = async () => {
    if (pendingEntries.length === 0) return;
    const msg = filter === 'PENDING' 
      ? `Are you sure you want to approve ALL search results matching your current search?`
      : `This will approve all PENDING entries matching your current search. Proceed?`;
      
    if (!window.confirm(msg)) return;

    setIsBulkApproving(true);
    try {
      await timeEntriesAPI.bulkApproveFiltered({ search: activeSearch });
      fetchPendingApprovals();
    } catch (error) {
      console.error('Bulk approval failed:', error);
      alert('Failed to approve entries');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === pendingEntries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingEntries.map(e => e.id));
    }
  };

  const toggleSelectEntry = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setActiveSearch(searchQuery);
    }
  };

const getStatusBadge = (status) => `badge badge-${status.toLowerCase()}`;

  const renderPagination = () => {
    const { page: currPage, size: currSize, total_records, total_pages } = paginationInfo;
    if (total_pages <= 1) return null;

    const startRecord = (currPage - 1) * currSize + 1;
    const endRecord = Math.min(currPage * currSize, total_records);

    // Generate page numbers to display
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="premium-pagination">
        <div className="premium-pagination-info">
          Showing <span style={{ fontWeight: 600 }}>{startRecord}</span> to{' '}
          <span style={{ fontWeight: 600 }}>{endRecord}</span> of{' '}
          <span style={{ fontWeight: 600 }}>{total_records}</span> entries
        </div>
        <div className="premium-pagination-controls">
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(1)}
            disabled={currPage === 1}
            title="First Page"
          >
            &lt;&lt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            disabled={currPage === 1}
            title="Previous Page"
          >
            &lt;
          </button>
          
          {startPage > 1 && (
            <>
              <button className="premium-pagination-btn" onClick={() => setPage(1)}>1</button>
              {startPage > 2 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
            </>
          )}

          {pageNumbers.map(num => (
            <button
              key={num}
              className={`premium-pagination-btn ${currPage === num ? 'active' : ''}`}
              onClick={() => setPage(num)}
            >
              {num}
            </button>
          ))}

          {endPage < total_pages && (
            <>
              {endPage < total_pages - 1 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
              <button className="premium-pagination-btn" onClick={() => setPage(total_pages)}>{total_pages}</button>
            </>
          )}

          <button
            className="premium-pagination-btn"
            onClick={() => setPage(prev => Math.min(prev + 1, total_pages))}
            disabled={currPage === total_pages}
            title="Next Page"
          >
            &gt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(total_pages)}
            disabled={currPage === total_pages}
            title="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    );
  };

  const renderBlockView = () => {
    return (
      <div className="block-grid">
        {pendingEntries.map((entry) => {
          const isSelected = selectedIds.includes(entry.id);
          return (
            <div key={entry.id} className={`block-card ${isSelected ? 'row-selected' : ''}`} style={{ borderLeft: isSelected ? '4px solid var(--primary)' : '' }}>
              <div className="block-card-header">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={isSelected} 
                    onChange={() => toggleSelectEntry(entry.id)}
                    disabled={entry.status !== 'PENDING'}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="avatar avatar-sm">
                    {entry.user_name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="block-card-title">{entry.user_name}</div>
                    <div className="block-card-subtitle">{entry.work_date}</div>
                  </div>
                </div>
                <div>
                  <span className={getStatusBadge(entry.status)}>{entry.status}</span>
                </div>
              </div>
              <div className="block-card-body">
                <div className="block-card-field">
                  <span className="block-card-label">Project:</span>
                  <span className="block-card-value">{entry.project_name}</span>
                </div>
                {entry.activity_type && (
                  <div className="block-card-field">
                    <span className="block-card-label">Activity:</span>
                    <span className="block-card-value" style={{ textTransform: 'capitalize' }}>
                      {entry.activity_type.replace('_', ' ')}
                    </span>
                  </div>
                )}
                <div className="block-card-field">
                  <span className="block-card-label">Hours:</span>
                  <span className="block-card-value" style={{ fontSize: '15px', color: 'var(--primary)', fontWeight: 'bold' }}>
                    {entry.hours}h
                  </span>
                </div>
                <div 
                  className="block-card-notes"
                  onClick={() => {
                    setSelectedEntry(entry);
                    setShowDetailModal(true);
                  }}
                  title="Click to view full details"
                >
                  {entry.notes || <span className="italic text-slate-300">No notes provided</span>}
                </div>
              </div>
              <div className="block-card-footer">
                {entry.status === 'PENDING' && (
                  <div className="flex gap-2">
                    {entry.can_approve !== false && (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleApprove(entry.id)}
                          title="Approve"
                        >
                          <Check size={16} /> Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => { setSelectedEntry(entry); setShowRejectModal(true); }}
                          title="Reject"
                        >
                          <X size={16} /> Reject
                        </button>
                      </>
                    )}
                    <button
                      className="btn btn-sm btn-secondary"
                      style={{ background: '#cbd5e1', color: '#334155' }}
                      onClick={() => { setSelectedEntry(entry); setShowCommentsModal(true); }}
                      title="Comments"
                    >
                      <MessageSquare size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>Approvals</h2>
          <p>Review and approve time entries</p>
        </div>
        <div className="header-actions" style={{ gap: '12px', alignItems: 'center' }}>
          <div className="search-container">
             <Search size={18} className="search-icon" />
             <input
               type="text"
               placeholder="Search employee, project, notes..."
               className="form-input"
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               onKeyDown={handleSearchKeyPress}
               style={{ paddingLeft: '40px', width: '250px', marginBottom: 0 }}
             />
          </div>
          <div className="flex gap-2" style={{ alignItems: 'center' }}>
            {selectedIds.length > 0 && (
              <button 
                className="btn btn-success" 
                onClick={handleBulkApprove}
                disabled={isBulkApproving}
              >
                Approve Selected ({selectedIds.length})
              </button>
            )}
            {selectedIds.length > 0 && (
              <button 
                className="btn btn-danger" 
                onClick={() => { setIsBulkAction(true); setShowRejectModal(true); }}
                disabled={isBulkApproving}
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                Reject Selected ({selectedIds.length})
              </button>
            )}
            {filter === 'PENDING' && pendingEntries.length > 0 && (
              <button 
                className="btn btn-secondary" 
                onClick={handleApproveAllFiltered}
                disabled={isBulkApproving}
                style={{ background: '#f1f5f9', color: '#0d9488', border: '1px solid #0d9488' }}
              >
                Approve All
              </button>
            )}
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="ALL">All</option>
            </select>
            
            <div className="view-toggle-container">
              <button 
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={16} />
              </button>
              <button 
                className={`view-toggle-btn ${viewMode === 'block' ? 'active' : ''}`}
                onClick={() => setViewMode('block')}
                title="Block/Grid View"
              >
                <LayoutGrid size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="page-content">
        {pendingEntries.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Check size={48} />
              </div>
              <div className="empty-state-title">All caught up!</div>
              <div className="empty-state-text">No pending approvals at this time.</div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title">
                {filter === 'PENDING' ? 'Pending Approvals' : 'Approvals History'} ({paginationInfo.total_records})
              </h3>
            </div>
            
            {renderPagination()}

            {viewMode === 'list' ? (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedIds.length === pendingEntries.length && pendingEntries.length > 0} 
                          onChange={toggleSelectAll}
                          disabled={filter !== 'PENDING' && filter !== 'ALL'}
                        />
                      </th>
                      <th>Employee</th>
                      <th>Project</th>
                      <th>Date</th>
                      <th>Hours</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingEntries.map((entry) => (
                      <tr key={entry.id} className={selectedIds.includes(entry.id) ? 'row-selected' : ''}>
                        <td>
                          <input 
                            type="checkbox" 
                            checked={selectedIds.includes(entry.id)} 
                            onChange={() => toggleSelectEntry(entry.id)}
                            disabled={entry.status !== 'PENDING'}
                          />
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="avatar avatar-sm">
                              {entry.user_name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <span>{entry.user_name}</span>
                          </div>
                        </td>
                        <td>{entry.project_name}</td>
                        <td>{entry.work_date}</td>
                        <td>{entry.hours}h</td>
                        <td 
                          onClick={() => { setSelectedEntry(entry); setShowDetailModal(true); }}
                          style={{ cursor: 'pointer', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title="Click to view details"
                        >
                          {entry.notes || <span className="text-slate-300 italic">-</span>}
                        </td>
                        <td>
                          {entry.status === 'PENDING' ? (
                            <div className="flex gap-2">
                              {entry.can_approve !== false && (
                                <>
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => handleApprove(entry.id)}
                                    title="Approve"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => { setSelectedEntry(entry); setShowRejectModal(true); }}
                                    title="Reject"
                                  >
                                    <X size={16} />
                                  </button>
                                </>
                              )}
                              <button
                                className="btn btn-sm btn-secondary"
                                style={{ background: '#cbd5e1', color: '#334155' }}
                                onClick={() => { setSelectedEntry(entry); setShowCommentsModal(true); }}
                                title="Comments"
                              >
                                <MessageSquare size={16} />
                              </button>
                            </div>
                          ) : (
                            <span className={getStatusBadge(entry.status)}>{entry.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '0 24px 24px' }}>
                {renderBlockView()}
              </div>
            )}

            {renderPagination()}
          </div>
        )}

        {/* Quick Actions */}
        <div className="stats-grid" style={{ marginTop: '24px' }}>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon orange">
                <Eye size={24} />
              </div>
            </div>
            <div className="stat-value">{pendingEntries.length}</div>
            <div className="stat-label">Pending Approvals</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon teal">
                <Filter size={24} />
              </div>
            </div>
            <div className="stat-value">
              {pendingEntries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0).toFixed(1)}h
            </div>
            <div className="stat-label">Total Hours Pending</div>
          </div>
        </div>
      </div>

      {/* Reject Drawer */}
      {showRejectModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="premium-drawer" onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{isBulkAction ? `Reject ${selectedIds.length} Entries` : 'Reject Time Entry'}</h3>
              <button className="btn btn-icon" onClick={() => { setShowRejectModal(false); setIsBulkAction(false); }}>
                <X size={24} />
              </button>
            </div>
            
            <div className="drawer-body">
              {!isBulkAction && (
                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Employee</label>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{selectedEntry?.user_name}</div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Hours</label>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--primary)' }}>{selectedEntry?.hours}h</div>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: '12px', marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Project</label>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{selectedEntry?.project_name}</div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Reason for Rejection</label>
                <textarea
                  className="form-input"
                  placeholder="Please provide a clear reason for the employee to correct this entry..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={6}
                  required
                  style={{ resize: 'none' }}
                />
              </div>
              
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '8px', borderLeft: '4px solid #ef4444', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Logging a rejection will notify the employee immediately via their dashboard.
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={() => { setShowRejectModal(false); setIsBulkAction(false); }}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleReject} style={{ backgroundColor: '#ef4444', color: 'white' }}>
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsModal && selectedEntry && (
        <div className="premium-drawer-overlay" onClick={() => setShowCommentsModal(false)}>
          <div className="premium-drawer" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Comments for {selectedEntry.user_name}</h3>
              <button className="btn-icon" onClick={() => setShowCommentsModal(false)}><X size={20} /></button>
            </div>
            <div className="drawer-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {(!selectedEntry.comments || selectedEntry.comments.length === 0) ? (
                <p className="text-secondary" style={{ fontStyle: 'italic', padding: '20px 0', textAlign: 'center' }}>No comments yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {selectedEntry.comments.map(c => (
                    <div key={c.id} style={{ padding: '12px', background: 'var(--surface-color)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '13px' }}>{c.user_name}</strong>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px' }}>{c.comment}</p>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
                <textarea 
                  className="form-input" 
                  rows="3" 
                  placeholder="Type a comment..."
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>
            <div className="drawer-footer">
              <button className="btn btn-secondary" onClick={() => setShowCommentsModal(false)}>Close</button>
              <button 
                className="btn btn-primary" 
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Post Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Entry Modal */}
      {showDetailModal && selectedEntry && (
        <div className="detail-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="detail-modal-card" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <div className="detail-modal-title">Time Entry Details</div>
              <button className="btn-icon" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-modal-field">
                <div className="detail-modal-label">Employee</div>
                <div className="detail-modal-value font-semibold">{selectedEntry.user_name}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Date</div>
                  <div className="detail-modal-value">{selectedEntry.work_date}</div>
                </div>
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Hours</div>
                  <div className="detail-modal-value font-bold text-teal-600" style={{ fontSize: '16px' }}>{selectedEntry.hours}h</div>
                </div>
              </div>
              <div className="detail-modal-field">
                <div className="detail-modal-label">Project</div>
                <div className="detail-modal-value font-semibold">{selectedEntry.project_name}</div>
              </div>
              {selectedEntry.activity_type && (
                <div className="detail-modal-field">
                  <div className="detail-modal-label">Activity Type</div>
                  <div className="detail-modal-value" style={{ textTransform: 'capitalize' }}>
                    {selectedEntry.activity_type.replace('_', ' ')}
                  </div>
                </div>
              )}
              <div className="detail-modal-field">
                <div className="detail-modal-label">Status</div>
                <div className="detail-modal-value">
                  <span className={getStatusBadge(selectedEntry.status)}>{selectedEntry.status}</span>
                  {selectedEntry.approved_by_name && (
                    <span className="text-xs text-slate-500 italic ml-2">
                      by {selectedEntry.approved_by_name}
                    </span>
                  )}
                </div>
              </div>
              {selectedEntry.status === 'REJECTED' && selectedEntry.rejection_reason && (
                <div className="detail-modal-field">
                  <div className="detail-modal-label" style={{ color: 'var(--error)' }}>Rejection Reason</div>
                  <div className="detail-modal-value" style={{ padding: '8px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '6px', borderLeft: '3px solid var(--error)' }}>
                    {selectedEntry.rejection_reason}
                  </div>
                </div>
              )}
              <div className="detail-modal-field">
                <div className="detail-modal-label">Notes</div>
                <div className="detail-modal-notes">
                  {selectedEntry.notes || <span className="italic text-slate-400">No notes provided</span>}
                </div>
              </div>
            </div>
            <div className="detail-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Approvals;










